import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Mail, CheckCircle2, Wrench, Compass, Users, Sparkles } from "lucide-react";

export default function Login({ pionierMode: pionierModeProp = false } = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorParam = searchParams.get("error");
  // Sprint M5.24.3 (24.07.2026) — Pionier-safe Login.
  // Aktiv, wenn die Route `/pionier-login` gerendert wird ODER die URL
  // `?ref=pionier` mitbringt (z. B. aus KlickTipp-Broadcast-Mails).
  // In diesem Modus:
  //   - Header laeuft in `variant="pionier"` → kein „Registrieren"-CTA
  //   - Copy adressiert bestehende Pioniere direkt
  const pionierMode = pionierModeProp || searchParams.get("ref") === "pionier";
  // Sprint 12+M5.11 (19.07.2026) — Harte Trennung: Pioniere sind KEINE
  // Free/Paid-Login-User. Wenn ein eingeloggter Pionier hier landet (z. B.
  // durch alten Bookmark oder verlorene Session), sofort auf die Pionier-
  // Bridge redirecten, damit er nicht ins Free/Paid-Flow-Loop rutscht.
  // WICHTIG: Redirect MUSS nach allen Hook-Aufrufen stehen (Rules of Hooks).
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [ttlMinutes, setTtlMinutes] = useState(15);
  const [isDev, setIsDev] = useState(false);
  const [devLoading, setDevLoading] = useState(false);
  const [fokustypUsers, setFokustypUsers] = useState([]);
  const [selectedFokustypEmail, setSelectedFokustypEmail] = useState("");

  // Sprint 12+M5.11 — Guard NACH allen Hooks (Rules of Hooks).
  const isPionier =
    user && typeof user === "object" && user.is_pionier === true;

  useEffect(() => {
    let cancelled = false;
    api.get("/config").then(
      ({ data }) => {
        if (!cancelled) setIsDev(!!data?.test_login_enabled);
      },
      () => { /* swallow */ },
    );
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isDev) return;
    let cancelled = false;
    api.get("/auth/test/users").then(
      ({ data }) => {
        if (!cancelled) setFokustypUsers(data?.users || []);
      },
      () => { /* swallow */ },
    );
    return () => { cancelled = true; };
  }, [isDev]);

  const handleTestLogin = async () => {
    setError("");
    setDevLoading(true);
    try {
      await api.post("/auth/test/login", { username: "Test", password: "54321" });
      // Hotfix v14d — Cockpit ist neuer Default (statt /dashboard).
      window.location.href = "/cockpit";
      return;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        setError("Zu viele Test-Login-Versuche. Bitte 5 Minuten warten.");
      } else if (status === 404) {
        setError("Test-Login ist in dieser Umgebung deaktiviert.");
      } else {
        setError("Test-Login fehlgeschlagen. Bitte erneut versuchen.");
      }
      setDevLoading(false);
    }
  };

  const handleFokustypLogin = async () => {
    if (!selectedFokustypEmail) return;
    setError("");
    setDevLoading(true);
    try {
      await api.post("/auth/test/login", {
        email: selectedFokustypEmail,
        password: "test12345",
      });
      // Hotfix v14d — Cockpit ist neuer Default (statt /dashboard).
      window.location.href = "/cockpit";
      return;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setError("Test-User-Credentials ungültig.");
      } else if (status === 429) {
        setError("Zu viele Test-Login-Versuche. Bitte 5 Minuten warten.");
      } else if (status === 404) {
        setError("Test-Login ist in dieser Umgebung deaktiviert.");
      } else {
        setError("Test-Login fehlgeschlagen. Bitte erneut versuchen.");
      }
      setDevLoading(false);
    }
  };

  const errorMessage =
    errorParam === "expired"
      ? "Dein Magic Link ist abgelaufen. Bitte fordere einen neuen an."
      : errorParam === "used"
      ? "Dieser Magic Link wurde bereits verwendet. Bitte fordere einen neuen an."
      : "";
  // Post-Launch-H8 (10.02.2026) — Erfolgs-Banner nach sauberem Logout
  // (RedesignTopBar + PageProfil setzen ?logout=1 nach AuthContext.logout()).
  const logoutSuccess = searchParams.get("logout") === "1";
  // Weg von der KT400-Pause-Seite: nur bestehende Konten, neutrale Rückmeldung.
  const pauseMode = searchParams.get("from") === "kt400-pause";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/magic/request", {
        email: email.trim(),
        ...(pauseMode ? { login_only: true, silent_unknown: true } : {}),
      });
      setTtlMinutes(data?.expires_in_minutes || 15);
      setSent(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        setError(
          "Du hast in der letzten Stunde zu viele Magic Links angefordert. Bitte warte einen Moment.",
        );
      } else {
        setError(
          formatApiErrorDetail(e.response?.data?.detail) ||
            "Konnte den Magic Link nicht senden. Bitte erneut versuchen.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Sprint 12+M5.11 — Guard: Eingeloggter Pionier → Pionier-Bridge.
  // Sprint M5.37 (29.07.2026) — Block A/C: Auth-Gate für ALLE eingeloggten
  // User (nicht nur Pionier). Login-Seite darf niemals Cockpit-Ziel sein.
  // Muss NACH allen Hooks stehen (Rules of Hooks).
  if (isPionier) {
    return <Navigate to="/dashboard/pionier" replace />;
  }
  if (user && user.email) {
    return <Navigate to="/cockpit" replace />;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="login-page">
      <Header variant={pionierMode ? "pionier" : "login"} />
      <main className="flex-1 flex items-center justify-center px-6 py-12 sm:py-16">
        <div className="w-full max-w-md">
          {/* Sprint V35 (Sa 14.09.2026) — Kontext-Block oberhalb der Login-Karte.
              Andreas-Direktive: Pioniere kommen aus KT-Erinnerungs-Mails und
              wissen nicht mehr, was Flex4Success eigentlich ist. Der Kontext-
              Block liefert Orientierung, ohne die Login-Karte zu überladen.
              Wird nach Magic-Link-Send ausgeblendet. */}
          {!sent && (
            <div className="mb-6 text-center" data-testid="login-context-block">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4563BB]">
                Flex4Success · Dein PocketCoach
              </p>
              <h2 className="mt-2 text-lg sm:text-xl font-extrabold text-slate-900 leading-snug">
                {pionierMode
                  ? "Schön, dass du zurück bist."
                  : "Deine Werkstatt für wirkungsvolle Kommunikation."}
              </h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-sm mx-auto">
                {pionierMode
                  ? "Hier findest du dein Fokustyp-Profil, dein Gegenüber-Radar und die Türöffner-Werkstatt — genau da, wo du sie beim letzten Mal gelassen hast."
                  : "Erkenne deinen Fokustyp, verstehe dein Gegenüber und formuliere Sätze, die wirklich ankommen — nach dem Vier-Fokustypen-Modell."}
              </p>
              <ul className="mt-4 flex items-center justify-center gap-4 sm:gap-6 flex-wrap" data-testid="login-context-bullets">
                <li className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700">
                  <Compass className="w-3.5 h-3.5 text-[#4563BB]" aria-hidden="true" />
                  Fokustyp-Profil
                </li>
                <li className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700">
                  <Users className="w-3.5 h-3.5 text-[#4563BB]" aria-hidden="true" />
                  Gegenüber-Radar
                </li>
                <li className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700">
                  <Sparkles className="w-3.5 h-3.5 text-[#4563BB]" aria-hidden="true" />
                  Türöffner-Sätze
                </li>
              </ul>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_30px_-10px_rgba(15,23,42,0.1)] p-8 sm:p-10">
            {sent ? (
              <div className="text-center" data-testid="magic-link-sent-view">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <h1 className="mt-5 text-2xl font-extrabold tracking-tight text-slate-900">
                  Check dein Postfach.
                </h1>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                  {pauseMode ? (
                    <span data-testid="login-sent-neutral">
                      {`Wenn zu dieser Adresse ein Konto besteht, ist ein Login-Link unterwegs. Er ist ${ttlMinutes} Minuten gültig.`}
                    </span>
                  ) : (
                    <>
                      {"Ein Magic Link ist auf dem Weg zu "}
                      <span className="font-semibold text-slate-900">{email}</span>
                      {`. Klick auf den Button in der Mail — der Link ist ${ttlMinutes} Minuten gültig.`}
                    </>
                  )}
                </p>
                <Button
                  onClick={() => {
                    setSent(false);
                    setEmail("");
                  }}
                  variant="ghost"
                  data-testid="magic-link-resend-btn"
                  className="mt-7 rounded-xl text-slate-600 hover:text-slate-900 font-semibold"
                >
                  Andere E-Mail verwenden
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-7">
                  <div className="w-12 h-12 mx-auto rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#1E40AF]" />
                  </div>
                  <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-slate-900">
                    {pionierMode ? "Willkommen zurück, Pionier." : "Willkommen zurück."}
                  </h1>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {pionierMode
                      ? "Gib deine E-Mail ein — wir schicken dir deinen Magic-Link direkt in dein Postfach."
                      : "Ein Magic Link zum Einloggen ist auf dem Weg — keine Passwörter, keine Datenkrümel."}
                  </p>
                </div>

                {errorMessage && (
                  <div
                    className="mb-5 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2"
                    data-testid="magic-link-banner"
                  >
                    {errorMessage}
                  </div>
                )}
                {logoutSuccess && !errorMessage && (
                  <div
                    className="mb-5 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-start gap-2"
                    data-testid="logout-success-banner"
                  >
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                    <span>
                      Du wurdest abgemeldet. Bis bald! Trag einfach deine
                      E-Mail unten ein, um dich wieder einzuloggen.
                    </span>
                  </div>
                )}

                <form onSubmit={onSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="email" className="text-slate-700 font-semibold">
                      E-Mail
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="magic-email-input"
                      className="mt-2 rounded-xl border-slate-200 focus-visible:ring-[#1E40AF] h-11"
                      placeholder="du@beispiel.de"
                    />
                  </div>

                  {error && (
                    <div
                      className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
                      data-testid="magic-error"
                    >
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading || !email}
                    data-testid="magic-submit-btn"
                    className="w-full rounded-xl bg-[#1E40AF] hover:bg-blue-900 text-white font-semibold h-11"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Magic Link anfordern"
                    )}
                  </Button>

                  <p
                    className="text-center text-xs text-slate-500 leading-relaxed"
                    data-testid="magic-micro-copy"
                  >
                    Mit Klick akzeptierst du unsere{" "}
                    <a
                      href="https://effektiv-kommunizieren.de/datenschutz"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-[#1E40AF] hover:underline"
                    >
                      Datenschutzrichtlinien
                    </a>
                    .
                  </p>
                </form>

                {isDev && (
                  <div className="mt-6 pt-6 border-t border-dashed border-slate-200" data-testid="dev-login-section">
                    <button
                      type="button"
                      onClick={handleTestLogin}
                      disabled={devLoading}
                      data-testid="dev-test-login-btn"
                      className="w-full flex items-center justify-center gap-2 rounded-xl bg-[var(--fts-neutral-50)] hover:bg-slate-100 text-[var(--fts-text-muted)] hover:text-[var(--fts-text-primary)] font-semibold text-sm h-10 transition-colors border border-slate-200"
                    >
                      {devLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Wrench className="w-3.5 h-3.5" />
                          Schnell-Login als Test-User (nur Dev/Preview)
                        </>
                      )}
                    </button>

                    {fokustypUsers.length > 0 && (
                      <div className="mt-4" data-testid="dev-fokustyp-login-block">
                        <Label
                          htmlFor="dev-fokustyp-dropdown"
                          className="text-xs font-semibold text-slate-500 uppercase tracking-wide"
                        >
                          Fokustyp-Test-User (4 Profile)
                        </Label>
                        <select
                          id="dev-fokustyp-dropdown"
                          data-testid="dev-login-dropdown"
                          value={selectedFokustypEmail}
                          onChange={(e) => setSelectedFokustypEmail(e.target.value)}
                          className="mt-2 w-full h-11 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                        >
                          <option value="">— Test-User wählen —</option>
                          {fokustypUsers.map((u) => (
                            <option
                              key={u.email}
                              value={u.email}
                              data-testid={`dev-login-option-${u.fokustyp_dominant}`}
                            >
                              {u.name} ({u.fokustyp_dominant})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleFokustypLogin}
                          disabled={devLoading || !selectedFokustypEmail}
                          data-testid="dev-login-button"
                          className="mt-3 w-full rounded-xl bg-slate-700 hover:bg-slate-900 text-white font-semibold text-sm h-10 disabled:opacity-50"
                        >
                          {devLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin inline" />
                          ) : (
                            "Test-Login mit Fokustyp"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
